import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom'
import Loader from '../../component/layout/Loader'
import { toast } from 'react-toastify'
import IconCaretsDown from '../../component/icon/IconCaretsDown'
import AnimateHeight from 'react-animate-height'
import Dropdown from '../../component/Dropdown'
import i18next from 'i18next'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-date-picker'
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import api from '../../api/api'
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";


const HomeworkAddEdit = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const params = useParams()
    const [loading, setLoading] = useState(false)
    const [isEditMode, setIsEditMode] = useState(false)
    const [homeworkData, setHomeworkData] = useState({
        title: "",
        description: "",
        start_date: new Date(),
        end_date: new Date(),
        assigned_to: [],
        status: "",
    })
    const [studentList, setStudentList] = useState<any[]>([])
    const [courseInfo, setCourseInfo] = useState<any>(null)
    const [branchInfo, setBranchInfo] = useState<any>(null)
    const [studentSubmissions, setStudentSubmissions] = useState<any>({})
    const [fileAttachments, setFileAttachments] = useState<File[]>([])
    const [existingFiles, setExistingFiles] = useState<any[]>([])
    const [selectedFile, setSelectedFile] = useState<any>(null)
    const [showFileModal, setShowFileModal] = useState(false)

    // Get URL parameters and route params
    const homeworkId = params.id
    const courseId = searchParams.get('course_id')
    const branchId = searchParams.get('branch_id')
    const branchName = searchParams.get('branch_name')
    const timeslotScheduleId = searchParams.get('timeslot_schedule_id')

    // Determine if we're in edit mode
    useEffect(() => {
        setIsEditMode(!!homeworkId)
    }, [homeworkId])

    // Fetch students for the course and branch
    const getStudents = () => {
        if (!courseId || !branchId) {
            console.log("Missing course_id or branch_id parameters")
            return
        }

        setLoading(true)
        api.get(`${process.env.REACT_APP_API_BASE_URL}/homework/coach/get-students/?course_id=${courseId}&branch_id=${branchId}`)
            .then(res => {
                setLoading(false)
                console.log("Students response:", res.data.data)
                if (res && res.data && res.data.data) {
                    setStudentList(res.data.data.data)
                    setCourseInfo(res.data.data.course_info)
                    setBranchInfo(res.data.data.branch_info)
                }
            })
            .catch(err => {
                setLoading(false)
                console.log("Error fetching students:", err)
                toast.error("Failed to load students")
            })
    }

    // Fetch homework details for edit mode
    const getHomeworkDetails = () => {
        if (!homeworkId) return

        setLoading(true)
        api.get(`${process.env.REACT_APP_API_BASE_URL}/homework/${homeworkId}/`)
            .then(res => {
                setLoading(false)
                // console.log("Homework details:", res.data.data)
                if (res && res.data && res.data.data) {
                    const homework = res.data.data
                    
                    // Extract student IDs from assignments array
                    const assignedStudentIds = homework.assignments ? 
                        homework.assignments.map((assignment: any) => assignment.student_id) : []
                    
                    setHomeworkData({
                        title: homework.title || "",
                        description: homework.description || "",
                        start_date: homework.start_date ? new Date(homework.start_date) : new Date(),
                        end_date: homework.end_date ? new Date(homework.end_date) : new Date(),
                        assigned_to: assignedStudentIds,
                        status: homework.status
                    })
                    
                    // Set course and branch info from homework details
                    setCourseInfo({
                        id: homework.course_id,
                        name: homework.course_name
                    })
                    setBranchInfo({
                        id: homework.branch_id,
                        name: homework.branch_name
                    })
                    
                    // Fetch students for this course and branch to populate the dropdown
                    if (homework.course_id && homework.branch_id) {
                        getStudentsForEdit(homework.course_id, homework.branch_id)
                    }

                    // Use submissions already included in homework.assignments (avoid extra API calls)
                    if (homework.assignments && homework.assignments.length > 0) {
                        const submissionsMap: any = {}
                        homework.assignments.forEach((assignment: any) => {
                            submissionsMap[assignment.student_id] = assignment.submission || null
                        })
                        setStudentSubmissions(submissionsMap)
                    }

                    // Set existing files from homework data
                    if (homework.files && homework.files.length > 0) {
                        setExistingFiles(homework.files)
                    }
                }
            })
            .catch(err => {
                setLoading(false)
                console.log("Error fetching homework details:", err)
                toast.error("Failed to load homework details")
            })
    }

    // Fetch students for edit mode using homework's course and branch
    const getStudentsForEdit = (courseId: number, branchId: number) => {
        api.get(`${process.env.REACT_APP_API_BASE_URL}/homework/coach/get-students/?course_id=${courseId}&branch_id=${branchId}`)
            .then(res => {
                console.log("Students for edit mode:", res.data.data)
                if (res && res.data && res.data.data) {
                    setStudentList(res.data.data.data)
                }
            })
            .catch(err => {
                console.log("Error fetching students for edit:", err)
                toast.error("Failed to load students list")
            })
    }

    useEffect(() => {
        if (isEditMode) {
            getHomeworkDetails()
        } else {
            getStudents()
        }
    }, [isEditMode, courseId, branchId, homeworkId])

    // Handle form submission
    const handleSubmit = (values: any) => {
        console.log("Form values:", values)
        // Prevent updating a closed homework
        if (isEditMode && homeworkData && homeworkData.status === 'close') {
            toast.error("This homework is closed and cannot be updated.")
            return
        }

        try {
            const start = values.start_date instanceof Date ? values.start_date : new Date(values.start_date)
            const end = values.end_date instanceof Date ? values.end_date : new Date(values.end_date)
            if (isNaN(start.getTime()) || isNaN(end.getTime())) {
                toast.error("Invalid date(s) provided")
                return
            }
            if (end <= start) {
                toast.error("Due date must be later than Start date")
                return
            }
        } catch (e) {
            toast.error("Invalid date(s) provided")
            return
        }
        
        if (isEditMode) {
            // Update existing homework
            const formData = new FormData()
            formData.append('title', values.title)
            formData.append('description', values.description)
            formData.append('start_date', values.start_date.toISOString())
            formData.append('end_date', values.end_date.toISOString())
            formData.append('is_active', 'true')
            
            values.assigned_to.forEach((id: string) => {
                formData.append('student_ids', id)
            })
            
            fileAttachments.forEach((file) => {
                formData.append('file_attachments', file)
            })
            
            // console.log("Update FormData:", formData)
            
            setLoading(true)
            api.patch(`${process.env.REACT_APP_API_BASE_URL}/homework/update/${homeworkId}/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(res => {
                    setLoading(false)
                    console.log("Homework updated successfully:", res.data)
                    toast.success("Homework updated successfully!")
                    navigate("/homework")
                })
                .catch(err => {
                    setLoading(false)
                    console.log("Error updating homework:", err)
                    toast.error("Failed to update homework. Please try again.")
                })
        } else {
            // Create new homework
            const formData = new FormData()
            formData.append('title', values.title)
            formData.append('description', values.description)
            formData.append('course', courseId || '0')
            formData.append('branch', branchId || '0')
            formData.append('timeslot_schedule_id', timeslotScheduleId || '0')
            formData.append('start_date', values.start_date.toISOString())
            formData.append('end_date', values.end_date.toISOString())
            formData.append('is_active', 'true')
            
            values.assigned_to.forEach((id: string) => {
                formData.append('student_ids', id)
            })
            
            fileAttachments.forEach((file) => {
                formData.append('file_attachments', file)
            })
            
            console.log("Create FormData:", formData)
            
            setLoading(true)
            api.post(`${process.env.REACT_APP_API_BASE_URL}/homework/create/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            })
                .then(res => {
                    setLoading(false)
                    console.log("Homework created successfully:", res.data)
                    toast.success("Homework created successfully!")
                    navigate("/homework")
                })
                .catch(err => {
                    setLoading(false)
                    console.log("Error creating homework:", err)
                    toast.error("Failed to create homework. Please try again.")
                })
        }
    }

    // Handle file attachment changes
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || [])
        const validFiles = files.filter(file => {
            const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
            return validTypes.includes(file.type)
        })
        
        if (validFiles.length + fileAttachments.length + existingFiles.length > 10) {
            toast.error("Maximum 10 files allowed (including existing files)")
            return
        }
        
        setFileAttachments(prev => [...prev, ...validFiles])
    }

    const removeFile = (index: number) => {
        setFileAttachments(prev => prev.filter((_, i) => i !== index))
    }

    // Handle file view in modal
    const viewFile = (file: any) => {
        setSelectedFile(file)
        setShowFileModal(true)
    }

    const closeFileModal = () => {
        setSelectedFile(null)
        setShowFileModal(false)
    }

    // Handle close homework (set is_active to false)
    const handleCloseHomework = () => {
        if (!homeworkId) return

        if (window.confirm("Are you sure you want to close this homework? This action cannot be undone.")) {
            setLoading(true)

            const formData = new FormData()
            formData.append('is_active', 'false')

            api.patch(`${process.env.REACT_APP_API_BASE_URL}/homework/update/${homeworkId}/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            }) .then(res => {
                setLoading(false)
                console.log("Homework closed successfully:", res.data)
                toast.success("Homework closed successfully!")
                navigate("/homework")
            }) .catch(err => {
                setLoading(false)
                console.log("Error closing homework:", err)
                toast.error("Failed to close homework. Please try again.")
            })
        }
    }

    const validationSchema = Yup.object().shape({
        title: Yup.string().required(t("validation.required")),
        description: Yup.string().required(t("validation.required")),
        start_date: Yup.date().required(t("validation.required")),
        end_date: Yup.date().required(t("validation.required")),
        assigned_to: Yup.array().min(1, t("validation.required")).required(t("validation.required")),
    });

    return (
        <div>
            {loading && <Loader/>}
            <ul className="flex space-x-2 rtl:space-x-reverse">
                <li>
                    <button 
                        onClick={() => navigate('/homework')}
                        className="text-primary hover:underline cursor-pointer"
                    >
                        Homework
                    </button>
                </li>
                <li className="before:content-['/'] ltr:before:mr-2 rtl:before:ml-2">
                    <span>{isEditMode ? 'Edit' : 'Create'}</span>
                </li>
            </ul>
            
            {/* Course and Branch Info */}
            {(homeworkData && homeworkData.title && homeworkData.description) && (
                <div className="mt-6 max-w-full mx-auto">
                    <div className="bg-white border-l-4 border-orange-400 rounded-lg px-2 py-3 shadow-sm">
                        <div className="space-y-1">
                            <h3 className="text-lg font-semibold text-black">
                                {homeworkData.title}
                            </h3>
                            <div className="text-gray-700">
                                <p>{homeworkData.description}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="pt-5">
                <Formik
                    enableReinitialize
                    initialValues={{
                        title: homeworkData.title || "",
                        description: homeworkData.description || "",
                        start_date: homeworkData.start_date || new Date(),
                        end_date: homeworkData.end_date || new Date(),
                        assigned_to: homeworkData.assigned_to || [],
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ setFieldValue, values }) => (
                        <Form className="max-w-full mx-auto space-y-6">
                            <div className="space-y-6">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-lg font-medium">Title</label>
                                        <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
                                        </svg>
                                    </div>
                                    <Field name="title" type="text" placeholder="Homework title" className="form-input w-full rounded-lg border-gray-300" />
                                    <ErrorMessage name="title" component="div" className="text-red-500 text-sm mt-1" />
                                </div>
                                
                                <div>
                                    <label className="text-lg font-medium mb-2 block">Description</label>
                                    <Field name="description" as="textarea" placeholder="Homework Description" rows="6" className="form-input w-full rounded-lg border-gray-300 resize-none" />
                                    <ErrorMessage name="description" component="div" className="text-red-500 text-sm mt-1" />
                                </div>

                                {/* File Attachments Section */}
                                <div>
                                    <div className="mb-4">
                                        <input
                                            id="file-upload"
                                            type="file"
                                            multiple
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={handleFileChange}
                                            className="hidden"
                                            disabled={fileAttachments.length + existingFiles.length >= 10 || homeworkData.status === 'close'}
                                        />
                                        <label
                                            htmlFor="file-upload"
                                            title={homeworkData.status === 'close' ? 'Homework is closed' : 'Upload files'}
                                            aria-disabled={homeworkData.status === 'close'}
                                            className={`w-full bg-black text-white py-3 px-6 rounded-full text-center ${homeworkData.status === 'close' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-800'} transition-colors flex items-center justify-center font-medium`}
                                        >
                                            Upload (max. 10)
                                        </label>
                                        <div className="text-sm text-gray-600 mt-2 text-center">
                                            * image/pdf/doc/video
                                        </div>
                                    </div>
                                    
                                    {/* Display existing files (in edit mode) */}
                                    {isEditMode && existingFiles.length > 0 && (
                                        <div className="space-y-3">
                                            {existingFiles.map((file, index) => (
                                                <div key={file.id} className="flex items-center justify-between p-3 bg-gray-300 rounded-lg">
                                                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                                                        <span className="text-sm font-medium truncate">{file.file_name}</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => viewFile(file)}
                                                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                    >
                                                        View
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Display selected files */}
                                    {fileAttachments.length > 0 && (
                                        <div className="space-y-3">
                                            {fileAttachments.map((file, index) => (
                                                <div key={index} className="flex items-center justify-between p-3 bg-gray-300 rounded-lg">
                                                    <div className="flex items-center space-x-3">
                                                        <span className="text-sm font-medium truncate">{file.name}</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFile(index)}
                                                        className="text-red-500 hover:text-red-700 text-sm font-medium"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Date Section */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-lg font-medium mb-2 block">Start Date:</label>
                                        <DatePicker 
                                            onChange={(date) => setFieldValue("start_date", date)} 
                                            value={values.start_date} 
                                            className="w-full px-4 py-2 border-2 border-primary rounded-lg" 
                                        />
                                        <ErrorMessage name="start_date" component="div" className="text-red-500 text-sm mt-1" />
                                    </div>
                                    
                                    <div>
                                        <label className="text-lg font-medium mb-2 block">Due date:</label>
                                        <DatePicker 
                                            onChange={(date) => setFieldValue("end_date", date)} 
                                            value={values.end_date} 
                                            className="w-full px-4 py-2 border-2 border-yellow-500 rounded-lg" 
                                        />
                                        <ErrorMessage name="end_date" component="div" className="text-red-500 text-sm mt-1" />
                                    </div>
                                </div>

                                <div>
                                    <div className="space-y-4">

                                        {/* Select All option */}
                                        <div className="flex items-center space-x-3 p-2">
                                            <Field name="assigned_to">
                                                {({ field, form }: any) => {
                                                    const allSelected = studentList.length > 0 && field.value.length === studentList.length
                                                    return (
                                                        <div 
                                                            className="flex items-center space-x-3 cursor-pointer"
                                                            onClick={() => {
                                                                const allIds = studentList.map(s => s.id)
                                                                form.setFieldValue('assigned_to', allSelected ? [] : allIds)
                                                            }}
                                                        >
                                                            <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${allSelected ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                                                                {allSelected && (
                                                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                                                    </svg>
                                                                )}
                                                            </div>
                                                            <span className="font-medium text-gray-900">Select All</span>
                                                        </div>
                                                    )
                                                }}
                                            </Field>
                                        </div>

                                        {/* Student list */}
                                        <div className="space-y-2 max-h-64 overflow-y-auto">
                                            <Field name="assigned_to">
                                                {({ field, form }: any) => {
                                                    return (
                                                    <>
                                                        {studentList.map((student: any) => {
                                                            const isSelected = field.value.includes(student.id.toString()) || field.value.includes(student.id)
                                                            return (
                                                                <div 
                                                                    key={student.id}
                                                                    className="flex items-center space-x-3 p-2 hover:bg-gray-50 cursor-pointer"
                                                                    onClick={() => {
                                                                        const currentValues = field.value || []
                                                                        const studentIdStr = student.id.toString()
                                                                        const studentIdNum = student.id
                                                                        if (isSelected) {
                                                                            // Remove both string and number versions
                                                                            form.setFieldValue('assigned_to', currentValues.filter((id: any) => 
                                                                                id !== studentIdStr && id !== studentIdNum
                                                                            ))
                                                                        } else {
                                                                            form.setFieldValue('assigned_to', [...currentValues, studentIdNum])
                                                                        }
                                                                    }}
                                                                >
                                                                    <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${isSelected ? 'border-primary bg-primary' : 'border-gray-300'}`}>
                                                                        {isSelected && (
                                                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                                                            </svg>
                                                                        )}
                                                                    </div>
                                                                    <span className="text-gray-900">
                                                                        {student.name} {student.chinese_name ? `(${student.chinese_name})` : ''}
                                                                    </span>
                                                                </div>
                                                            )
                                                        })}
                                                    </>
                                                )}}
                                            </Field>
                                        </div>
                                    </div>
                                    <ErrorMessage name="assigned_to" component="div" className="text-red-500 text-sm mt-1" />
                                </div>
                                
                                <div className="pt-2 pb-4">
                                    {isEditMode ? (
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                                            <div className="flex gap-3">
                                                <button type="button" onClick={() => navigate("/homework")} className="flex-1 sm:flex-none px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors">
                                                    Back
                                                </button>
                                                <button
                                                    type="submit"
                                                    disabled={homeworkData.status === 'close'}
                                                    className={`flex-1 sm:flex-none px-6 py-2 bg-primary text-white rounded-lg transition-colors ${homeworkData.status === 'close' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    Update
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleCloseHomework}
                                                disabled={homeworkData.status === 'close'}
                                                className={`px-6 py-2 bg-red-600 text-white rounded-lg transition-colors ${homeworkData.status === 'close' ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700'}`}
                                            >
                                                Close Homework
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex gap-3">
                                            <button type="button" onClick={() => navigate("/homework")} className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors">
                                                Back
                                            </button>
                                            <button type="submit" className="px-6 py-2 bg-primary text-white rounded-lg transition-colors">
                                                Create
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
            {/* Assigned Students List (visible in edit mode) */}
            {isEditMode && Array.isArray(homeworkData.assigned_to) && homeworkData.assigned_to.length > 0 && (
                <div className="mt-4 mb-6">
                    <h3 className="font-semibold text-lg mb-4">Assigned Students</h3>
                    <div className="space-y-4">
                        {homeworkData.assigned_to.map((studentId: any) => {
                            const student = studentList.find((s: any) => s.id == studentId)
                            const submission = studentSubmissions[studentId]
                            // Consider submission as rated if a 'grade' field exists (not null/undefined)
                            const isRated = submission && (submission.grade !== null && submission.grade !== undefined)
                            const iconColor = isRated ? 'bg-primary' : 'bg-orange-400'
                            return (
                                <div key={studentId} className="flex items-center gap-4 p-4 bg-white rounded-lg shadow border">
                                    <div className={`w-12 h-12 flex items-center justify-center rounded-full ${iconColor}`}>
                                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" stroke="#fff" strokeWidth="2"/></svg>
                                    </div>
                                    <div className="flex-1">
                                        {submission ? (
                                            <button
                                                className="text-lg font-semibold text-left hover:underline"
                                                style={{ color: 'inherit', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                                                onClick={() => navigate(`/homework/student/${studentId}/details/${homeworkId}`)}
                                            >
                                                {student ? `${(student as any).name}${(student as any).chinese_name ? ` (${(student as any).chinese_name})` : ''}` : '[Student Name]'}
                                            </button>
                                        ) : (
                                            <div className="text-lg font-semibold text-left text-gray-400" style={{ padding: 0 }}>
                                                {student ? `${(student as any).name}${(student as any).chinese_name ? ` (${(student as any).chinese_name})` : ''}` : '[Student Name]'}
                                            </div>
                                        )}
                                        <div className="text-gray-600 text-sm mt-1">
                                            Submission: {submission && submission.submitted_at ?
                                                new Date(submission.submitted_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* File View Modal */}
            {showFileModal && selectedFile && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-full max-h-[90vh] w-full flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-lg font-semibold">{selectedFile.file_name}</h3>
                            <button
                                onClick={closeFileModal}
                                className="text-gray-500 hover:text-gray-700 text-xl"
                            >
                                ×
                            </button>
                        </div>
                        <div className="flex-1 p-4 overflow-auto">
                            {selectedFile.file_name?.toLowerCase().includes('.pdf') ? (
                                <iframe
                                    src={selectedFile.presigned_url}
                                    className="w-full h-96"
                                    title="PDF Viewer"
                                />
                            ) : (
                                <img
                                    src={selectedFile.presigned_url}
                                    alt={selectedFile.file_name}
                                    className="max-w-full h-auto mx-auto"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement
                                        target.style.display = 'none'
                                        target.parentElement!.innerHTML = `
                                            <div class="text-center text-red-500 p-8">
                                                <p>Unable to load file: ${selectedFile.file_name}</p>
                                                <p class="text-sm text-gray-500 mt-2">The file may be corrupted or the link has expired.</p>
                                            </div>
                                        `
                                    }}
                                />
                            )}
                        </div>
                        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                            <span className="text-sm text-gray-500">
                                Size: {(selectedFile.file_size / 1024).toFixed(1)} KB
                            </span>
                            <div className="flex gap-2">
                                {/* <a
                                    href={selectedFile.presigned_url}
                                    download={selectedFile.file_name}
                                    className="btn btn-secondary btn-sm"
                                >
                                    Download
                                </a> */}
                                <button
                                    onClick={closeFileModal}
                                    className="btn btn-primary btn-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default HomeworkAddEdit